import React, { useState } from 'react'

import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import CheckBox from '../../controls/CheckBox'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'
import ProviderAutocomplete from '../../ProviderAutocomplete'
import ProviderForm from '../../ProviderForm'

export default {
  name: 'Durable Medical Equipment',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    equipment: validation
      .arrayOf(
        validation.object({
          item: validation.name('Item').required(),
          dateReceived: validation.date('Date Received'),
          dateRepaired: validation.date('Date Repaired'),
          dateWarranty: validation.date('Date of Warranty Expiration'),
          provider: validation.shortText('Provider'),
          related: validation.shortText('Related to Injury'),
        }),
      )
      .nullable(),
  })
}

function Component({ addItemToArray, removeItemFromArray, createInputProps, getValue, ErrorMessage, setModelValues }) {
  const [editingIndex, setEditingIndex] = useState(null)
  const equipment = getValue('equipment', [])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [activeEquipmentIndex, setActiveEquipmentIndex] = useState(null)

  const handleProviderSelect = (index, provider) => {
    if (provider) {
      setModelValues([
        [`equipment[${index}].provider`, provider.name || provider.organizationName],
        [`equipment[${index}].providerId`, provider.id],
      ])
    }
  }

  const handleCreateNewProvider = index => {
    setActiveEquipmentIndex(index)
    setShowCreateModal(true)
  }

  const handleAddEquipment = () => {
    const nextIndex = equipment.length
    addItemToArray('equipment', {})
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    removeItemFromArray('equipment', index)
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div>
      <h4 className="pb-3">Durable Medical Equipment</h4>
      {equipment.map((x, index) => {
        const namePrefix = `equipment[${index}]`
        const isEditing = editingIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <Equipment
              namePrefix={namePrefix}
              createInputProps={createInputProps}
              getValue={getValue}
              ErrorMessage={ErrorMessage}
              isEditing={isEditing}
              onEdit={() => setEditingIndex(index)}
              onDone={() => setEditingIndex(null)}
              handleRemove={() => handleRemove(index)}
              onProviderSelect={p => handleProviderSelect(index, p)}
              onCreateProvider={() => handleCreateNewProvider(index)}
              providerValue={x.provider}
              isRental={x.isRental}
              itemData={x}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="addEquipmentButton" onClick={handleAddEquipment}>
        Add Durable Medical Equipment
      </AddButton>

      <ProviderForm
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={newProvider => {
          if (activeEquipmentIndex !== null) {
            handleProviderSelect(activeEquipmentIndex, newProvider)
          }
          setShowCreateModal(false)
        }}
        initialType="Equipment"
      />
    </div>
  )
}

function Equipment({
  namePrefix,
  createInputProps,
  ErrorMessage,
  handleRemove,
  onProviderSelect,
  onCreateProvider,
  providerValue,
  isRental,
  itemData = {},
  isEditing,
  onEdit,
  onDone,
}) {
  const item = itemData.item || ''
  const provider = itemData.provider || ''
  const rentalStartDate = itemData.rentalStartDate || ''
  const rentalEndDate = itemData.rentalEndDate || ''
  const dateReceived = itemData.dateReceived || ''
  const dateWarranty = itemData.dateWarranty || ''
  const relatedToInjury = itemData.relatedToInjury

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{item || <em className="text-muted">Untitled Equipment</em>}</span>
            {provider && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Provider: {provider}
              </span>
            )}
            {isRental ? (
              <span className="badge badge-secondary ml-3">
                Rental
                {rentalStartDate || rentalEndDate
                  ? ` (${[rentalStartDate, rentalEndDate].filter(Boolean).join(' - ')})`
                  : ''}
              </span>
            ) : (
              <>
                {dateReceived && (
                  <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                    Received: {dateReceived}
                  </span>
                )}
                {dateWarranty && (
                  <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                    Warranty: {dateWarranty}
                  </span>
                )}
              </>
            )}
            {relatedToInjury && <span className="badge badge-info ml-3">Related to Injury</span>}
          </div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this equipment">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-row">
        <div className="form-group col-md-9">
          <label className="form-label" htmlFor={`${namePrefix}.item`}>
            Item
          </label>
          <input
            type="text"
            id={`${namePrefix}.item`}
            className="form-control"
            maxLength={validation.maxLengths.name}
            {...createInputProps(`${namePrefix}.item`)}
          />
          <ErrorMessage name={`${namePrefix}.item`} />
        </div>
      </div>

      <div className="form-row mb-3">
        <div className="col-12">
          <CheckBox {...createInputProps(`${namePrefix}.isRental`)}>Is this a rental?</CheckBox>
        </div>
      </div>

      {isRental ? (
        <div className="form-row">
          <div className="form-group col-md-3">
            <label className="form-label" htmlFor={`${namePrefix}.rentalStartDate`}>
              Rental Start Date
            </label>
            <DatePicker {...createInputProps(`${namePrefix}.rentalStartDate`)} />
          </div>
          <div className="form-group col-md-3">
            <label className="form-label" htmlFor={`${namePrefix}.rentalEndDate`}>
              Rental End Date
            </label>
            <DatePicker {...createInputProps(`${namePrefix}.rentalEndDate`)} />
          </div>
        </div>
      ) : (
        <div className="form-row">
          <div className="form-group col-md-3">
            <label className="form-label" htmlFor={`${namePrefix}.dateReceived`}>
              Date Received
            </label>
            <DatePicker {...createInputProps(`${namePrefix}.dateReceived`)} />
          </div>
          <div className="form-group col-md-3">
            <label className="form-label" htmlFor={`${namePrefix}.dateRepaired`}>
              Date Repaired
            </label>
            <DatePicker {...createInputProps(`${namePrefix}.dateRepaired`)} />
          </div>
          <div className="form-group col-md-3">
            <label className="form-label" htmlFor={`${namePrefix}.dateWarranty`}>
              Date of Warranty Expiration
            </label>
            <DatePicker {...createInputProps(`${namePrefix}.dateWarranty`)} />
          </div>
          <ErrorMessage name={`${namePrefix}.dateReceived`} />
          <ErrorMessage name={`${namePrefix}.dateRepaired`} />
          <ErrorMessage name={`${namePrefix}.dateWarranty`} />
        </div>
      )}

      <div className="form-row">
        <div className="form-group col-md-9">
          <label className="form-label" htmlFor={`${namePrefix}.provider`}>
            Provider
          </label>
          {/* Use ProviderAutocomplete as Selector */}
          <div className="d-flex align-items-center">
            <div style={{ flex: 1 }}>
              <ProviderAutocomplete
                type="Equipment"
                onProviderSelect={onProviderSelect}
                onChange={() => {}} // No-op, managed by Select
                value={providerValue || ''} // Show current name
                placeholder="Search for a provider..."
              />
            </div>
            <div className="ml-3 white-space-nowrap">
              <span className="mr-1">or</span>
              <button type="button" className="btn btn-link p-0 align-baseline" onClick={onCreateProvider}>
                Create New Provider
              </button>
            </div>
          </div>
          <ErrorMessage name={`${namePrefix}.provider`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group align-center col-4 col-md-4">
          <CheckBox {...createInputProps(`${namePrefix}.relatedToInjury`)}>Related to Injury</CheckBox>
        </div>
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
